const en = {
  locale: "en",

  Header: {
    logout: "Log out",
  },

  LoginDialog: {
    title: "Log in",
    email: "E-mail",
    password: "Password",
    login: "Log in",
    loginError: "Could not log in with the provided credentials.",
    sessionExpired: "Your login session has expired. Please log in again.",
    loggedInAs: "Logged in as {{loggedInUser}}",
    switchUser: "Switch user",
  },

  HomeView: {
    title: "Adaptive Survey",
    create: "Create new survey",
  },

  CreateView: {
    title: "New Survey",
  },

  EditView: {
    loading: "Loading survey...",
    loadError: "Could not load survey.",
  },

  ResultsView: {
    loading: "Loading survey...",
    loadError: "Could not load survey.",
  },

  Results: {
    backToAllSurveys: "Back to all surveys",
    responses: ["{{n}} response", "{{n}} responses"],
    resultsInfo: "Click the bars to filter data.",
    download: "Download data",
    print: "Print report",
    printWarning: "Please print this report by using the Print report button.",
    includeInPrint: "Include data table in print",
    multiselect: "(multiple allowed)",
    yes: "Yes",
    no: "No",
    noAnswer: "No answer",
    created: "Submit date",
    clearFilter: "Clear filter",
    clearAllFilters: "Clear all filters",
    filters: "Data is filtered",
    count: "Count",
    openImage: "View image",
    editValue: "Edit {{label}}",
    admin: "(admin)",
    save: "Save",
    cancel: "Cancel",
  },

  CollectionView: {
    loading: "Loading survey...",
    loadError: "Could not load survey.",
    surveyNotActive: "This survey is not available",
    pleasePublishFirst:
      "Survey has been unpublished and is no longer available.",
    goBack: "Go back",
    saving: "Submitting your response",
    saveErrorTitle: "An error occurred!",
    saveErrorText:
      "Your response was not saved.\n\nPlease try again or contact the publisher of this survey to get help.",
  },

  Editor: {
    save: "Save",
    saved: "Saved",
    unsaved: "No changes",
    backToAllSurveys: "Back to all surveys",
    goToSurvey: "Go to survey",
    publish: "Publish",
    unpublish: "Unpublish",
    preview: "Preview",
    livePreview: "Live preview",
    mobilePreview: "Mobile preview",
    desktopPreview: "Desktop preview",
    saving: "Saving survey...",
    saveErrorTitle: "Could not save survey",
    saveError: "The survey contains errors. Please correct the following:",
    unsavedChangesConfirm: "You have unsaved changes. Continue anyway?",
    unsavedChangesTitle: "You have unsaved changes",
    unsavedChanges: "Please save before continuing.",
    metaDataError:
      "There was a problem loading survey metadata. Please try again.",
    discard: "Discard changes",
    invalidWarning: ["{{n}} problem", "{{n}} problems"],
  },

  Metadata: {
    title: "Settings",
    editMetadata: "Edit settings",
    info: "Please describe the new survey to make it recognizable in the survey administrator. <b>This will not be visible for participants.</b>",
    createdBy: "Created by",
    created: "Date created",
    metaCompletionRedirectInfo:
      "Instead of displaying the standard completion page, the user will be sent to the specified URL.",
    public: "Public",
    private: "Private",
    cancel: "Cancel",
    ok: "Go to schema designer",
  },

  DesignSchema: {
    title: "Design schema",
    info: "Add the title, texts, images and any fields the participant should be presented with.",
    plainAddGroup: "Add group",
    wizardAddGroup: "Add page",
    plainAddSection: "Add element",
    wizardAddSection: "Add element (own page)",
    plainAddSectionInGroup: "Add element to group",
    wizardAddSectionInGroup: "Add element to page",
    addSectionToGetStarted: "Add an element to get started!",
    standardFieldsInfo:
      "<h3>Standard fields</h3><p>Note that some fields are automatically created for you:<ul><li>A unique id per response</li><li>When the response was collected</li></ul><p>You do not need to add these yourself.",
  },

  SectionForm: {
    back: "Back",
    next: "Next",
    cancel: "Cancel",
    add: "Add",
    save: "Save",
    type: "Field type:",
    edit: "Edit",
    addSection: "Add element",
    editSection: "Edit field",
    input: "Select field type",
    display: "Select presentational element",
    geometry: "Select geometry type",
    advanced: "Advanced settings",
    advancedSummary: "Field name: {{columnName}} {{admin}}",
    administrative: "Administrative",
    gdprWarning:
      "<b>Warning:</b> Text fields may be used to enter personal information. Take care what kind of information you are requesting and whether privacy legislation (like GDPR) may trigger requirements regarding data processing and consent.",
    emailWarning:
      "<b>Warning:</b> An e-mail address is personal information. Please consider the need for this and take into consideration relevant privacy legislation (like GDPR).",
    booleanRequiredInfo:
      "<b>Warning:</b> If you set this to required, the checkbox MUST be checked before the user can continue. This is useful for consent collection.",
  },

  SectionColumnName: {
    override: "Customize",
  },

  Section: {
    section: "Field",
    edit: "Edit",
    delete: "Delete",
    confirmDelete: "Click again to confirm.",
    type: "Field type",
    columnName: "Field name",
    required: "Required",
    administrative: "Administrative",
    admin: "Admin",
    yes: "Yes",
    no: "No",
    moveUp: "Move up",
    moveDown: "Move down",
  },

  Layout: {
    plain: "Plain, single page",
    plainDescription:
      "<h4>Plain layout</h4>" +
      "<p>The entire schema will be presented on a single page.</p>" +
      "<p>Use groups to split the schema into logical parts.</p>",
    wizard: "Wizard, page per element/group",
    wizardDescription:
      "<h4>Wizard layout</h4>" +
      "<p>Each element will be presented on a separate page, with back/continue buttons.</p>" +
      "<p>To display multiple elements on a single page, add a group to group them together.</p>" +
      "<p>The title will only be displayed on the first page. Use group titles or headings to add titles to subsequent pages.</p>",
  },

  LayoutRenderer: {
    emptySchema: "The schema is empty! Add at least one element.",
    submit: "Submit",
  },

  GroupSet: {
    delete: "Delete group (content is not deleted)",
    confirmDelete: "Click again to confirm.",
    moveUp: "Move group up",
    moveDown: "Move group down",
  },

  SectionType: {
    undefinedDescription: "Please pick an element type to see description.",
    display: "Presentation",
    displayDescription:
      "Add headings, text content or images displayed in the survey.",
    display_heading: "Heading/title",
    display_text: "Text content",
    display_image: "Image",
    input: "Other input fields",
    inputDescription:
      "Add fields where the participant can enter text, pick options from lists, upload an image, etc.",
    text: "Text (one line)",
    text_area: "Long text",
    email: "E-mail address",
    url: "Link/URL",
    boolean: "Checkbox (true/false)",
    integer: "Number (integer)",
    decimal: "Number (decimal)",
    date: "Date",
    list: "List (single select)",
    multiselect_list: "List (multi select)",
    media: "Image/\u00addocument upload",
    rating: "Rating",
    geometry: "Geometry",
    geometryDescription:
      "Add a map to the survey where the participant can draw a point, line or area.",
    geometry_point: "Point",
    geometry_line: "Line",
    geometry_polygon: "Area",

    foreign_key: "Foreign key",
    datetime: "Date/time",
    uuid: "UUID",
    dictionary: "Dictionary",
    multiselect_dictionary: "Dictionary (multi)",
  },

  MediaType: {
    image: "Image",
    document: "Document",
  },

  RatingSymbol: {
    heart: "Hearts",
    star: "Stars",
    smiley: "Smileys",
  },

  Form: {
    metaTitleLabel: "Survey name",
    metaDescriptionLabel: "Description",
    metaPublicDataLabel: "Collected data access level",
    metaCompletionRedirectUrlLabel: "Send to web site after completion",
    metaLanguageLabel: "Survey language",
    slugLabel: "Friendly survey name (used in the URL)",
    slugInfo:
      "This text will be used as part of the web address (URL) when the users access the survey. It must be unique, and should not be changed after the survey has been published. Must be all lowercase characters from a-z, numbers, underscores and dashes. Spaces are not allowed.",
    slugExample:
      "When published, the survey is available at: <code>{{url}}</code>",
    layoutLabel: "Layout",
    titleLabel: "Title",
    plainGroupTitleLabel: "Group title",
    wizardGroupTitleLabel: "Page title",
    columnNameLabel: "Field name (internal reference only)",
    columnNameInfo:
      "Must be all lowercase, contain no spaces, only contain characters from a-z, numbers and underscores, and start with a character. This must not be changed after someone has replied to the survey.",
    labelLabel: "Title",
    labelInfo:
      "This is the question or description that tells the user what to answer.",
    helpTextLabel: "Help text",
    helpTextInfo:
      "If the question requires a longer instruction, you can enter it here.",
    requiredLabel: "Required",
    administrativeLabel: "Administrative",
    administrativeInfo:
      "Administrative fields are not collected in the survey, but can be edited later in results or in Adaptive.",
    headingLabel: "Heading",
    contentLabel: "Text content",
    imageLabel: "Image",
    acceptLabel: "Upload type",
    minLabel: "Minimum value",
    maxLabel: "Maximum value",
    symbolLabel: "Symbol",
    ratingMaxLabel: "Number of options",
    decimalsLabel: "Maximum number of decimals",
    itemsLabel: "Options",
    itemsInfo:
      "Add the options presented to the user. Option texts must not contain comma (,).",
    multiLabel: "Multi-geometry",
    mapSettingsLabel: "Map settings",
    basemapLabel: "Select a background map",
    basemapInfo:
      "The map layer must support EPSG:3857 projection. If no background map is visible in the preview below, it cannot be used.",
    layerSelectTitle: "Select a map layer",
    summary: "Almost done!",
    summaryInfo:
      "Please review your answers. When ready, please click the submit button to send your answers to us.",
    submit: "Submit",
    add: "Add",
    next: "Continue",
    prev: "Back",
    optional: "optional",
    required: "required",
    mediaDocument: "Document",
    mediaUpload: "Upload",
    mediaRemove: "Remove",
    yes: "Yes",
    no: "No",
    answered: "Answered",
    unanswered: "No answer",
    noSelection: "No selection",
    defaultGeometryMissingInfo:
      'One of the geometry columns must be named "geom". This will be set as the default geometry column for the data set.',
    polygonInvalid: "An area must consist of at least three points.",
    lineInvalid: "A line must conist of at least two points.",
    formatLabel: "Select file format",
  },

  AccessLevelDescriptions: {
    trueDescription:
      "<p>The collected data will be accessible without any login or credentials.</p><p><strong>Warning</strong>: Do not use when collecting sensitive data!</p>",
    falseDescription:
      "<p>No collected data is accessible without logging in.</p><p>Parts of the data can still be published via a theme. Configure this and more specific access control in Adaptive Administrator.</p>",
  },

  Paging: {
    noResults: "No results",
    results: ["{{n}} result", "{{start}} to {{end}} of {{n}} results"],
  },

  ConfirmButton: {
    confirmText: "Click again to confirm",
  },

  PointGeometryPicker: {
    location: "Use my location",
    reset: "Remove point",
  },

  LineGeometryPicker: {
    location: "My location",
    reset: "Start over",
    undo: "Undo last",
    done: "Done",
  },

  SurveyActionList: {
    publishAction: "Publish this survey",
    publish: "Publish",
    unpublishAction: "Unpublish this survey",
    unpublish: "Unpublish",
    loadingSurveys: "Loading surveys...",
    loadError: "Something went wrong",
    retryLoad: "Retry",
    surveysLoadError: "Could not load your surveys.",
    publishError: "Could not publish/unpublish survey: ",
    openSurveyResponseUrl: "Open survey collection form",
    openSurvey: "Go to survey",
    editSurvey: "Edit",
    results: "Results",
    dateCreated: "Date created",
    createdBy: "Created by",
  },

  MapExtentControl: {
    extent: "Set initial map location",
    optional: "optional",
  },

  MapExtentPicker: {
    location: "Zoom to my location",
    useExtent: "Set initial extent",
    removeExtent: "Remove initial extent",
  },

  PolygonGeometryPicker: {
    location: "My location",
    reset: "Start over",
    undo: "Undo last",
    done: "Done",
  },

  SurveyCompleteView: {
    title: "Thank you!",
    youMayCloseTheWindow:
      'Your response to the survey "{{surveyTitle}}" is registered.\n\nYou may now close the browser window/tab.',
    loadError: "An error occurred",
    loading: "Loading",
    close: "Close",
  },

  ValidityDialog: {
    invalid: "Your survey has some problems",
    dataLoss: "Changes may cause data loss!",
    dataLossWarning:
      "If you continue, existing answers will lose any data entered in these field.",
    noSections: "Your survey has no fields users can fill in.",
    missingField: "{{name}} cannot be empty.",
    missingDefaultGeom:
      "Your survey contains geometry fields, but none have column name 'geom'. When including geometry fields, a main geometry field must be specified. Please make sure that one geometry field has column name 'geom'.",
    removedColumn: "Field {{label}} ('{{column}}') has been removed.",
    duplicateColumn: "Field {{label}} shares column name '{{column}}'.",
    invalidCast:
      "Field {{label}} ('{{column}}') was previously of type {{from}}, which cannot be changed to {{to}}. Please rename '{{column}}' to something else, or change its type.",
    continue: "Continue anyway",
    cancel: "Cancel",
  },

  RichText: {
    linkPrompt: "Add link",
    save: "Done",
    cancel: "Cancel",
    hrefLabel: "URL",
    targetLabel: "Target",
    targetReplace: " ",
    targetNew: "New tab",
    toggleBold: "Toggle bold",
    toggleItalic: "Toggle italic",
    toggleStrikethrough: "Toggle strikethrough",
    toggleBulletList: "Toggle bullet list",
    toggleOrderedList: "Toggle ordered list",
    setLink: "Set link",
    unsetLink: "Remove link",
  },

  ThemeModeSwitch: {
    useLightMode: "Switch to light mode",
    useDarkMode: "Switch to dark mode",
  },
};

export type SurveyTranslations = typeof en;

export default en;
